import React, { useState } from 'react';
import { Layout, Typography, Space, Flex, Divider, Card } from 'antd';
import { DownOutlined, SmileOutlined, MehOutlined, FrownOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { ProgressSteps } from '../../../Components/Progress';
import { RealTimeAssistantData } from './types';
import { getEmotionStatus, getEmotionIcon } from './utils';

const { Header } = Layout;

export interface RealTimeAssistantPanelProps {

    data: RealTimeAssistantData;
    isVisible?: boolean;
    className?: string;
    style?: React.CSSProperties;
    sections?: {
        emotion?: boolean;
        objectives?: boolean;
        activities?: boolean;
    };
    headerTitle?: string;
    onClose?: () => void;
}

/**
 * RealTimeAssistantPanel - Reusable panel component for displaying real-time assistant data
 *
 * Displays emotion status, objectives progress, and conversation activities
 * with collapsible sections and dynamic styling based on data.
 */
export const RealTimeAssistantPanel: React.FC<RealTimeAssistantPanelProps> = ({
    data,
    isVisible = true,
    className = '',
    style = {},
    sections = { emotion: true, objectives: true, activities: true },
    headerTitle = 'Real-time Assistant'
}) => {
    const [isEmotionCollapsed, setIsEmotionCollapsed] = useState<boolean>(false);
    const [isObjectivesCollapsed, setIsObjectivesCollapsed] = useState<boolean>(false);
    const [isConversationCollapsed, setIsConversationCollapsed] = useState<boolean>(false);

    // Don't render if not visible
    if (isVisible === false) {
        return null;
    }

    return (
        <div
            className={`realtime-assistant-panel ${className}`}
            style={{
                flex: 1,
                height: '100%',
                backgroundColor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                minWidth: '294px',
                overflow: 'hidden',
                ...style
            }}
        >
            {/* Panel Header */}
            <Header style={{
                backgroundColor: '#ffffff',
                borderBottom: '1px solid rgba(33, 33, 47, 0.15)',
                padding: '24px',
                height: '87px',
                minHeight: '87px',
                maxHeight: '87px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start'
            }}>
                <Typography.Text
                    style={{
                        fontSize: '16px',
                        fontWeight: 600,
                        lineHeight: '24px',
                    }}
                >
                    {headerTitle}
                </Typography.Text>
            </Header>

            {/* Panel Content */}
            <Space direction="vertical" size={0} style={{ width: '100%', padding: '24px', flex: 1, overflowY: 'auto' }}>
                {/* Emotion Section */}
                {sections.emotion && (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Flex
                            justify="space-between"
                            align="center"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setIsEmotionCollapsed(!isEmotionCollapsed)}
                        >
                            <Typography.Text style={{
                                fontFamily: '"DM Sans", sans-serif',
                                fontSize: '14px',
                                fontWeight: 400,
                                lineHeight: '22px',
                                color: 'rgba(33, 33, 47, 0.88)'
                            }}>Emotion</Typography.Text>
                            <DownOutlined style={{
                                fontSize: '14px',
                                transform: isEmotionCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s ease'
                            }} />
                        </Flex>
                        {!isEmotionCollapsed && (() => {
                            const emotionStatus = getEmotionStatus(data.emotion.percentage);
                            const emotionIcon = getEmotionIcon(data.emotion.percentage);
                            const EmotionIconComponent = emotionIcon === 'smile' ? SmileOutlined : emotionIcon === 'meh' ? MehOutlined : FrownOutlined;

                            return (
                                <Flex align="center" gap={16} style={{ width: '100%' }}>
                                    <ProgressSteps
                                        percent={data.emotion.percentage}
                                        status={emotionStatus}
                                        showInfo={true}
                                    />
                                    <div style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '25.6px',
                                        backgroundColor: emotionStatus === 'success'
                                            ? 'rgba(170, 241, 137, 0.35)'
                                            : emotionStatus === 'poor'
                                                ? 'rgba(255, 77, 79, 0.35)'
                                                : 'rgba(227, 245, 113, 0.35)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        padding: '6.4px',
                                        boxSizing: 'border-box'
                                    }}>
                                        <EmotionIconComponent style={{
                                            color: emotionStatus === 'success'
                                                ? '#52c41a'
                                                : emotionStatus === 'poor'
                                                    ? '#ff4d4f'
                                                    : '#000000',
                                            fontSize: '19.2px'
                                        }} />
                                    </div>
                                </Flex>
                            );
                        })()}
                    </Space>
                )}

                {sections.emotion && (sections.objectives || sections.activities) && (
                    <div style={{ padding: '16px 0' }}>
                        <Divider style={{ margin: 0 }} />
                    </div>
                )}

                {/* Objectives Section */}
                {sections.objectives && (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Flex
                            justify="space-between"
                            align="center"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setIsObjectivesCollapsed(!isObjectivesCollapsed)}
                        >
                            <Typography.Text style={{
                                fontFamily: '"DM Sans", sans-serif',
                                fontSize: '14px',
                                fontWeight: 400,
                                lineHeight: '22px',
                                color: 'rgba(33, 33, 47, 0.88)'
                            }}>Objectives</Typography.Text>
                            <DownOutlined style={{
                                fontSize: '14px',
                                transform: isObjectivesCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s ease'
                            }} />
                        </Flex>
                        {!isObjectivesCollapsed && (() => {
                            // Filter logic: Always show 3 items if possible
                            // Priority: Next uncompleted objectives
                            // Context: Show max 2 most recent completed objectives

                            const completedItems = data.objectives.items.filter(i => i.completed);
                            const uncompletedItems = data.objectives.items.filter(i => !i.completed);

                            // Get up to 2 most recent completed items (from the end of the completed list)
                            const completedToShow = completedItems.slice(-2);

                            // Calculate remaining slots to fill 3 items total
                            const remainingSlots = 3 - completedToShow.length;

                            // Get next uncompleted items to fill the slots
                            const uncompletedToShow = uncompletedItems.slice(0, remainingSlots);

                            // If we still don't have 3 items (e.g. strict filtering left us short), 
                            // we can try to pull more from uncompleted if available, although logic above usually covers it.
                            // But if we have 0 completed, we show 3 uncompleted.
                            // If we have 1 completed, we show 2 uncompleted.
                            // If we have 2 completed, we show 1 uncompleted.

                            // Combine list maintaining order: Oldest completed -> Newest completed -> Next uncompleted
                            const displayItems = [...completedToShow, ...uncompletedToShow];

                            return (
                                <>
                                    <ProgressSteps
                                        percent={data.objectives.progress}
                                        status={getEmotionStatus(data.objectives.progress)}
                                        showInfo={true}
                                    />
                                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                        {displayItems.length > 0 ? (
                                            displayItems.map((objective) => (
                                                <Card
                                                    key={objective.id}
                                                    size="small"
                                                    style={{
                                                        backgroundColor: objective.completed ? '#f6f0ff' : 'rgba(33, 33, 47, 0.02)',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        padding: '4px 8px'
                                                    }}
                                                    bodyStyle={{ padding: 0 }}
                                                >
                                                    <Flex align="center" gap={8}>
                                                        {objective.completed && (
                                                            <CheckCircleOutlined
                                                                style={{
                                                                    color: '#7e70b3',
                                                                    fontSize: '16px'
                                                                }}
                                                            />
                                                        )}
                                                        <Typography.Text
                                                            style={{
                                                                color: objective.completed ? '#7e70b3' : 'rgba(33, 33, 47, 0.65)',
                                                                fontSize: '14px',
                                                                fontFamily: '"DM Sans", sans-serif',
                                                                fontWeight: 400,
                                                                lineHeight: '22px'
                                                            }}
                                                        >
                                                            {objective.title}
                                                        </Typography.Text>
                                                    </Flex>
                                                </Card>
                                            ))
                                        ) : (
                                            <Typography.Text type="secondary" style={{ fontSize: '13px', padding: '8px 0', display: 'block', textAlign: 'center' }}>
                                                No objectives active
                                            </Typography.Text>
                                        )}
                                    </Space>
                                </>
                            );
                        })()}
                    </Space>
                )}

                {sections.objectives && sections.activities && (
                    <div style={{ padding: '16px 0' }}>
                        <Divider style={{ margin: 0 }} />
                    </div>
                )}

                {/* Conversation Activity Section */}
                {sections.activities && (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Flex
                            justify="space-between"
                            align="center"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setIsConversationCollapsed(!isConversationCollapsed)}
                        >
                            <Typography.Text style={{
                                fontFamily: '"DM Sans", sans-serif',
                                fontSize: '14px',
                                fontWeight: 400,
                                lineHeight: '22px',
                                color: 'rgba(33, 33, 47, 0.88)'
                            }}>Conversation Activity</Typography.Text>
                            <DownOutlined style={{
                                fontSize: '14px',
                                transform: isConversationCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s ease'
                            }} />
                        </Flex>
                        {!isConversationCollapsed && (
                            <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                {data.activities.map((activity) => (
                                    <Card
                                        key={activity.id}
                                        size="small"
                                        style={{
                                            backgroundColor: 'rgba(33, 33, 47, 0.06)',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '8px'
                                        }}
                                        bodyStyle={{ padding: 0 }}
                                    >
                                        <Typography.Text
                                            style={{
                                                fontSize: '14px',
                                                fontFamily: '"DM Sans", sans-serif',
                                                fontWeight: 400,
                                                lineHeight: '22px',
                                                color: 'rgba(33, 33, 47, 0.88)'
                                            }}
                                        >
                                            {activity.message}
                                        </Typography.Text>
                                    </Card>
                                ))}
                            </Space>
                        )}
                    </Space>
                )}
            </Space>
        </div>
    );
};

export default RealTimeAssistantPanel;
